/**
 * <TutorCommentField> — le mot du masteur, à la voix ou par écrit (organism).
 *
 * Deux surfaces de l'écran d'aujourd'hui le posent : le compte-rendu d'un élève (`CommentBlock` de
 * `StudentCloseOutCard`) et le ressenti sur la plateforme (`LiveSatisfactionSection`). Les mêmes
 * pièces, dans le même ordre :
 *  - la phrase qui dit qu'un vocal est arrivé vide, quand c'est le cas ;
 *  - en vocal : « Enregistrer un vocal », qui enregistre SUR PLACE (`<InlineVoiceRecorder>`, le
 *    `recorder` que l'écran pose sur le champ qui enregistre — plus de feuille, Wajih, 03/11 ; comme
 *    l'ancien bilan) ; « Terminer » et le vocal part ; pendant qu'il part, « Envoi du vocal… » ;
 *    puis le vocal envoyé, à réécouter, à réenregistrer ou à supprimer ;
 *  - en texte : le champ, qui s'enregistre à mesure qu'on écrit ;
 *  - un envoi qui a échoué : la phrase, et « Réessayer » — le mot reste sur la carte ;
 *  - la bascule Vocal / Texte, en pilules.
 *
 * Changer de mode efface ce qui a été dit dans l'autre : c'est l'écran qui le décide (et qui, pour
 * le ressenti, le confirme d'abord). Contrôlé : le mode, le texte, le vocal et l'envoi arrivent en
 * props ; la lecture du vocal aussi.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { Button } from "../../atoms/Buttons/Button"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { TextInput } from "../../atoms/TextInput"
import { Text } from "../../atoms/typography/Text"
import { WaitingText } from "../../atoms/WaitingText"
import { AlertCard } from "../../molecules/AlertCard"
import { MessageAttachment } from "../../molecules/MessageAttachment"
import { InlineVoiceRecorder, isRecorderOpen, type InlineVoiceRecorderProps } from "../InlineVoiceRecorder"
import { type UITutorComment, type UITutorCommentMode } from "../../../ports/primary/ui/types/tutorCloseOut"

export type TutorCommentFieldProps = {
    comment: UITutorComment
    /** La consigne au-dessus — « Fais un compte-rendu pour Jules ». Absente : rien. */
    title?: string
    /** La ligne sous la consigne — « Il sera utilisé pour rédiger un bilan de session aux parents ». */
    subtitle?: string
    placeholder?: string
    /** `comment.voiceUntranscribed` : ce qu'il faut en dire. */
    untranscribedMessage?: string
    onChangeMode: (mode: UITutorCommentMode) => void
    onChangeText: (text: string) => void
    /** Lance l'enregistrement, sur place ; avec un vocal déjà là, le réenregistre. */
    onRecordVoice: () => void
    /** L'enregistrement en cours sur CE champ. Absent : le bouton qui le lance. */
    recorder?: InlineVoiceRecorderProps | null
    onDeleteVoice: () => void
    /** Réécouter le vocal. */
    onToggleVoicePlay?: () => void
    voicePlaying?: boolean
    voiceProgress?: number
    /** `comment.send === "failed"` : renvoyer. */
    onRetry: () => void
    testID?: string
}

const TEXTS = {
    record: "Enregistrer un vocal",
    delete: "Supprimer",
    redo: "Réenregistrer",
    sending: "Envoi du vocal…",
    retry: "Réessayer",
    voice: "Vocal",
    text: "Texte",
}

export function TutorCommentField(props: TutorCommentFieldProps) {
    const {
        comment,
        title,
        subtitle,
        placeholder,
        untranscribedMessage,
        onChangeMode,
        onChangeText,
        onRecordVoice,
        recorder,
        onDeleteVoice,
        onToggleVoicePlay,
        voicePlaying = false,
        voiceProgress = 0,
        onRetry,
        testID,
    } = props

    // Un enregistrement ouvert sur ce champ : ni la bascule Vocal / Texte, ni rien qui le cacherait.
    const recording = isRecorderOpen(recorder)
    const voiceBody =
        recorder != null && recording ? (
            <InlineVoiceRecorder {...recorder} />
        ) : comment.send === "sending" ? (
            <WaitingText size="text-sm" color="text-muted">
                {TEXTS.sending}
            </WaitingText>
        ) : comment.voice != null ? (
            <Stack gap="2" style={styles.voice}>
                <MessageAttachment
                    attachment={{
                        kind: "voice",
                        uri: comment.voice.uri,
                        durationLabel: comment.voice.durationLabel,
                    }}
                    playing={voicePlaying}
                    progress={voiceProgress}
                    onTogglePlay={onToggleVoicePlay}
                />
                <Row gap="4" justify="center">
                    <TextButton label={TEXTS.redo} color="text-muted" onPress={onRecordVoice} />
                    <TextButton label={TEXTS.delete} color="text-muted" onPress={onDeleteVoice} />
                </Row>
            </Stack>
        ) : (
            <Button label={TEXTS.record} variant="outline" iconName="microphone" onPress={onRecordVoice} fullWidth />
        )

    return (
        <Stack gap="3" testID={testID}>
            {title != null && (
                <Stack gap="1" align="center">
                    <Text size="text-md" weight="bold" color="primary" center>
                        {title}
                    </Text>
                    {subtitle != null && (
                        <Text size="text-sm" color="text-muted" center>
                            {subtitle}
                        </Text>
                    )}
                </Stack>
            )}
            {comment.voiceUntranscribed === true && untranscribedMessage != null && (
                <AlertCard severity="alert" message={untranscribedMessage} />
            )}
            {comment.mode === "voice" ? (
                voiceBody
            ) : (
                <TextInput
                    value={comment.text}
                    onChangeText={onChangeText}
                    placeholder={placeholder}
                    multiline
                    numberOfLines={5}
                />
            )}
            {comment.send === "failed" && comment.sendError != null && (
                <AlertCard
                    severity="alert"
                    message={comment.sendError}
                    actions={<TextButton label={TEXTS.retry} onPress={onRetry} />}
                />
            )}
            <Row gap="2" justify="center">
                <ChoiceChip
                    variant="pill"
                    label={TEXTS.voice}
                    iconName="microphone"
                    selected={comment.mode === "voice"}
                    disabled={recording}
                    onPress={() => onChangeMode("voice")}
                />
                <ChoiceChip
                    variant="pill"
                    label={TEXTS.text}
                    iconName="keyboard"
                    selected={comment.mode === "text"}
                    disabled={recording}
                    onPress={() => onChangeMode("text")}
                />
            </Row>
        </Stack>
    )
}

const styles = StyleSheet.create((theme) => ({
    voice: {
        backgroundColor: theme.colors["surface-subtle"],
        borderRadius: theme.radius.md,
        paddingVertical: theme.spacing["2"],
        paddingHorizontal: theme.spacing["3"],
    },
}))
